// ============================================================================
// @jeraisonne/db/cards — schémas d'une SKILLCARD pédagogique (carte à jouer 63 × 88 mm).
// ============================================================================
// Une carte = UN outil pédagogique atomique (méthode, formule, théorème,
// tableau…). Le RECTO est l'outil lui-même, décrit par une liste de SECTIONS
// structurées (`cards.structured_content`, migration 0269) — jamais une image
// générée : formules en LaTeX, tableaux en cellules, figures en visuels
// structurés (réutilise les blocs visuels des exercices).
//
// ⚠️ MIROIR Deno : supabase/functions/_shared/card-content-schema.ts (validation
// stricte de la sortie LLM, visuels compris). Garder les bornes synchro — un test
// de parité côté _shared compare les deux.
// ============================================================================

import { z } from 'zod';

// ── Référentiels ────────────────────────────────────────────────────────────

export const CARD_TYPES = [
  'methode',
  'theoreme',
  'formule',
  'definition',
  'propriete',
  'identite_remarquable',
  'tableau',
  'procedure',
  'algorithme',
  'tableur',
  'arbre_decision',
  'schema_raisonnement',
  'technique_calcul',
] as const;
export type CardType = (typeof CARD_TYPES)[number];

export const CARD_TYPE_LABELS: Record<CardType, string> = {
  methode: 'Méthode',
  theoreme: 'Théorème',
  formule: 'Formule',
  definition: 'Définition',
  propriete: 'Propriété',
  identite_remarquable: 'Identité remarquable',
  tableau: 'Tableau de synthèse',
  procedure: 'Procédure',
  algorithme: 'Algorithme',
  tableur: 'Méthode tableur',
  arbre_decision: 'Arbre de décision',
  schema_raisonnement: 'Schéma de raisonnement',
  technique_calcul: 'Technique de calcul',
};

export const CARD_DIFFICULTIES = ['fondamentale', 'intermediaire', 'avancee', 'experte'] as const;
export type CardDifficulty = (typeof CARD_DIFFICULTIES)[number];
export const CARD_DIFFICULTY_LABELS: Record<CardDifficulty, string> = {
  fondamentale: 'Fondamentale',
  intermediaire: 'Intermédiaire',
  avancee: 'Avancée',
  experte: 'Experte',
};

export const CARD_IMPORTANCES = ['complementaire', 'importante', 'structurante'] as const;
export type CardImportance = (typeof CARD_IMPORTANCES)[number];
export const CARD_IMPORTANCE_LABELS: Record<CardImportance, string> = {
  complementaire: 'Complémentaire',
  importante: 'Importante',
  structurante: 'Structurante',
};

/**
 * Rareté d'une SKILLCARD (migration 0274) — les étoiles du verso : 1 (simple)
 * à 5 (légendaire). Choisie par le tuteur, proposée par « Générer la carte ».
 */
export const CARD_RARITIES = [
  'simple',
  'normale',
  'extraordinaire',
  'epique',
  'legendaire',
] as const;
export type CardRarity = (typeof CARD_RARITIES)[number];
export const CARD_RARITY_LABELS: Record<CardRarity, string> = {
  simple: 'Simple',
  normale: 'Normale',
  extraordinaire: 'Extraordinaire',
  epique: 'Épique',
  legendaire: 'Légendaire',
};
export const CARD_RARITY_STARS: Record<CardRarity, number> = {
  simple: 1,
  normale: 2,
  extraordinaire: 3,
  epique: 4,
  legendaire: 5,
};

/**
 * Rareté par défaut déduite de l'importance et de la difficulté. MIROIR du
 * backfill SQL de la migration 0274 (testé) — sert aussi de repli pour un
 * instantané publié antérieur à la rareté.
 */
export function defaultRarityFor(
  importance: CardImportance,
  difficulty: CardDifficulty,
): CardRarity {
  if (importance === 'complementaire') return 'simple';
  if (importance === 'importante') {
    return difficulty === 'avancee' || difficulty === 'experte' ? 'extraordinaire' : 'normale';
  }
  if (difficulty === 'experte') return 'legendaire';
  if (difficulty === 'avancee') return 'epique';
  return 'extraordinaire';
}

/**
 * Filiation (migration 0275) : une carte dérivée (A → B) vaut toujours une
 * étoile de plus que sa parente, plafond légendaire. MIROIR de la fonction SQL
 * `card_rarity_next` (testé) — le trigger serveur l'impose.
 */
export function nextCardRarity(rarity: CardRarity): CardRarity {
  const i = CARD_RARITIES.indexOf(rarity);
  return CARD_RARITIES[Math.min(CARD_RARITIES.length - 1, Math.max(0, i) + 1)]!;
}

/**
 * Filiation N-N (migration 0283) : une carte peut avoir PLUSIEURS mères ; sa
 * rareté vaut celle de la plus rare + 1 étoile. MIROIR de la fonction SQL
 * `card_rarity_from_parents` (testé). `null` = aucune mère (rareté libre).
 */
export function cardRarityFromParents(
  parents: readonly (CardRarity | null | undefined)[],
): CardRarity | null {
  if (parents.length === 0) return null;
  // Rareté inconnue = « normale », comme `card_rarity_rank` côté SQL.
  const rank = (r: CardRarity | null | undefined) => {
    const i = r ? CARD_RARITIES.indexOf(r) : -1;
    return i < 0 ? CARD_RARITIES.indexOf('normale') : i;
  };
  const top = Math.max(...parents.map(rank));
  return nextCardRarity(CARD_RARITIES[top]!);
}

/** Couleur de la carte : teinte du niveau le plus bas, ou bleu nuit forcé. */
export const CARD_COLOR_THEMES = ['level', 'night'] as const;
export type CardColorTheme = (typeof CARD_COLOR_THEMES)[number];
export const CARD_COLOR_THEME_LABELS: Record<CardColorTheme, string> = {
  level: 'Couleur du niveau',
  night: 'Bleu nuit',
};

/** Cycle de vie : DRAFT → GENERATED → REVIEW → PUBLISHED → ARCHIVED. */
export const CARD_STATUSES = ['draft', 'generated', 'review', 'published', 'archived'] as const;
export type CardStatus = (typeof CARD_STATUSES)[number];
export const CARD_STATUS_LABELS: Record<CardStatus, string> = {
  draft: 'Brouillon',
  generated: 'Générée',
  review: 'Relue',
  published: 'Publiée',
  archived: 'Archivée',
};

export const CARD_XP_MIN = 100;
export const CARD_XP_MAX = 100_000;
export const CARD_XP_DEFAULT = 1500;

// ── Sections du recto ───────────────────────────────────────────────────────

/** Bornes de longueur — calibrées pour rester lisibles sur une carte 63 × 88 mm. MIROIR Deno. */
export const CARD_LIMITS = {
  sections: 8,
  title: 60,
  stepItems: 8,
  stepItem: 200,
  latex: 300,
  variables: 6,
  variableSymbol: 30,
  variableMeaning: 120,
  conditions: 200,
  tableColumns: 5,
  tableRows: 8,
  tableCell: 80,
  markdown: 500,
  exampleStatement: 200,
  exampleSteps: 5,
  exampleStep: 160,
  exampleResult: 160,
  shortText: 240,
  caption: 120,
} as const;

/** Types de visuels autorisés sur une carte (figures uniquement : un tableau passe par `table`). */
export const CARD_VISUAL_TYPES = [
  'function_graph',
  'geometry_figure',
  'constrained_geometry',
  'vector_diagram',
] as const;
export type CardVisualType = (typeof CARD_VISUAL_TYPES)[number];

const L = CARD_LIMITS;
const optionalTitle = z.string().trim().max(L.title).optional();

export const stepsSectionSchema = z.object({
  kind: z.literal('steps'),
  title: optionalTitle,
  items: z.array(z.string().trim().min(1).max(L.stepItem)).min(1).max(L.stepItems),
});

export const formulaSectionSchema = z.object({
  kind: z.literal('formula'),
  title: optionalTitle,
  latex: z.string().trim().min(1).max(L.latex),
  variables: z
    .array(
      z.object({
        symbol: z.string().trim().min(1).max(L.variableSymbol),
        meaning: z.string().trim().min(1).max(L.variableMeaning),
      }),
    )
    .max(L.variables)
    .default([]),
  conditions: z.string().trim().max(L.conditions).optional(),
});

export const tableSectionSchema = z
  .object({
    kind: z.literal('table'),
    title: optionalTitle,
    headers: z.array(z.string().trim().max(L.tableCell)).min(1).max(L.tableColumns),
    rows: z
      .array(z.array(z.string().trim().max(L.tableCell)).min(1).max(L.tableColumns))
      .min(1)
      .max(L.tableRows),
  })
  .refine((t) => t.rows.every((r) => r.length === t.headers.length), {
    message: 'Chaque ligne du tableau doit avoir autant de cellules que d’en-têtes.',
  });

export const markdownSectionSchema = z.object({
  kind: z.literal('markdown'),
  title: optionalTitle,
  md: z.string().trim().min(1).max(L.markdown),
});

export const exampleSectionSchema = z.object({
  kind: z.literal('example'),
  statement: z.string().trim().min(1).max(L.exampleStatement),
  steps: z.array(z.string().trim().min(1).max(L.exampleStep)).max(L.exampleSteps).default([]),
  result: z.string().trim().max(L.exampleResult).optional(),
});

export const whenToUseSectionSchema = z.object({
  kind: z.literal('when_to_use'),
  md: z.string().trim().min(1).max(L.shortText),
});

export const pitfallSectionSchema = z.object({
  kind: z.literal('pitfall'),
  md: z.string().trim().min(1).max(L.shortText),
});

/**
 * Visuel structuré (graphique, figure…). Côté app on ne vérifie que le type :
 * la forme profonde est validée STRICTEMENT côté serveur à la génération
 * (`visualBlockSchema` du miroir Deno) et rendue par `VisualBlockRenderer`,
 * qui affiche un repli propre sur un bloc inattendu.
 */
export const visualSectionSchema = z.object({
  kind: z.literal('visual'),
  caption: z.string().trim().max(L.caption).optional(),
  block: z.object({ type: z.enum(CARD_VISUAL_TYPES) }).passthrough(),
});

export const cardSectionSchema = z.union([
  stepsSectionSchema,
  formulaSectionSchema,
  tableSectionSchema,
  markdownSectionSchema,
  exampleSectionSchema,
  whenToUseSectionSchema,
  pitfallSectionSchema,
  visualSectionSchema,
]);
export type CardSection = z.infer<typeof cardSectionSchema>;
export type CardSectionKind = CardSection['kind'];

export const CARD_SECTION_KINDS = [
  'steps',
  'formula',
  'table',
  'markdown',
  'example',
  'when_to_use',
  'pitfall',
  'visual',
] as const satisfies readonly CardSectionKind[];

export const CARD_SECTION_LABELS: Record<CardSectionKind, string> = {
  steps: 'Étapes',
  formula: 'Formule',
  table: 'Tableau',
  markdown: 'Texte',
  example: 'Exemple',
  when_to_use: 'Quand l’utiliser ?',
  pitfall: 'Attention / piège',
  visual: 'Schéma / figure',
};

/** Contenu du recto. Vide autorisé (brouillon) ; la publication exige ≥ 1 section. */
export const cardContentSchema = z.object({
  sections: z.array(cardSectionSchema).max(L.sections).default([]),
});
export type CardContent = z.infer<typeof cardContentSchema>;

export const EMPTY_CARD_CONTENT: CardContent = { sections: [] };

/** Lecture tolérante d'un jsonb `structured_content` : contenu invalide ⇒ vide (jamais d'écran cassé). */
export function parseCardContent(raw: unknown): CardContent {
  const parsed = cardContentSchema.safeParse(raw);
  return parsed.success ? parsed.data : EMPTY_CARD_CONTENT;
}

/** Section vide prête à éditer, pour le bouton « Ajouter une section ». */
export function emptySection(kind: CardSectionKind): CardSection {
  switch (kind) {
    case 'steps':
      return { kind, items: ['Première étape'] };
    case 'formula':
      return { kind, latex: 'a^2 + b^2 = c^2', variables: [] };
    case 'table':
      return { kind, headers: ['Colonne 1', 'Colonne 2'], rows: [['', '']] };
    case 'markdown':
      return { kind, md: 'Texte' };
    case 'example':
      return { kind, statement: 'Énoncé de l’exemple', steps: [] };
    case 'when_to_use':
      return { kind, md: 'Quand on rencontre…' };
    case 'pitfall':
      return { kind, md: 'Ne pas confondre…' };
    case 'visual':
      return {
        kind,
        block: {
          type: 'function_graph',
          viewport: { xMin: -5, xMax: 5, yMin: -2, yMax: 10 },
          functions: [{ id: 'f', expression: 'x^2', label: 'f' }],
          points: [],
        },
      };
  }
}
